import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
    Card,
    CardBody,
    CardHeader,
    Badge,
    Row,
    Col,
    Table,
    Container,
} from "reactstrap";
import PortalManagementService from "../../services/KangarooServices/Intelligence/PortalManagementService";
import BreadCrumb from "../../Components/Common/BreadCrumb";

const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
    });
};

const getInitials = (name = "") =>
    name
        .trim()
        .split(" ")
        .map((w) => w[0]?.toUpperCase())
        .join("")
        .slice(0, 2) || "?";

const ActionBadge = ({ action }) => {
    const map = {
        Activated: "success",
        Deactivated: "danger",
        Suspended: "warning",
    };
    return (
        <Badge color={map[action] || "secondary"} pill>
            {action}
        </Badge>
    );
};

const InfoRow = ({ label, value }) => (
    <Row className="mb-3 align-items-center">
        <Col xs="4" sm="3">
            <span className="text-muted small fw-semibold text-uppercase" style={{ letterSpacing: "0.05em" }}>
                {label}
            </span>
        </Col>
        <Col xs="8" sm="9">
            <span className="fw-medium">{value}</span>
        </Col>
    </Row>
);

const UserPortalDetail = () => {
    const [data, setData] = useState(null);



    const { id } = useParams();

    const fetchdata = async () => {
        try {
            const res = await PortalManagementService.getdetail(id);
            setData(res);
        } catch (error) {
            console.error("Error getting user:", error);
        }
    };

    useEffect(() => {
        fetchdata();
    }, [id]);

    const breadcrumbItems = [{ title: "Back To User List", link: "/portal-management" }];

    return (
        <div className="page-content " >
            <Container fluid>
                <BreadCrumb
                    title="User Portal Detail"
                    pageTitle="Portal Management"
                    breadcrumbItems={breadcrumbItems}
                />

                {/* ── Header Card ── */}
                <Card className="border-0 shadow-sm mb-4">
                    <CardBody className="p-4">
                        <div className="d-flex align-items-center gap-3 flex-wrap">
                            {/* Avatar */}
                            <div
                                className="d-flex align-items-center justify-content-center rounded-circle bg-primary text-white flex-shrink-0"
                                style={{ width: 56, height: 56, fontSize: 20, fontWeight: 600 }}
                            >
                                {getInitials(data?.fullName)}
                            </div>

                            {/* Name + badges */}
                            <div className="flex-grow-1">
                                <h5 className="mb-1 fw-semibold">{data?.fullName}</h5>
                                <div className="d-flex flex-wrap gap-2 align-items-center">
                                    <Badge color={data?.isActive ? "success" : "danger"} pill>
                                        {data?.isActive ? "Active" : "Inactive"}
                                    </Badge>
                                    <Badge color={data?.isAdminApproved ? "primary" : "warning"} pill>
                                        {data?.isAdminApproved ? "Admin Approved" : "Pending Approval"}
                                    </Badge>
                                    <Badge color="info" pill>
                                        {data?.portal}
                                    </Badge>
                                </div>
                            </div>

                            {/* Registered date */}
                            <div className="text-end text-muted small d-none d-sm-block">
                                <div>Registered Date:</div>
                                <div className="fw-medium text-dark">{formatDate(data?.registeredAt)}</div>
                            </div>
                        </div>
                    </CardBody>
                </Card>

                {/* ── Details Card ── */}
                <Card className="border-0 shadow-sm mb-4">
                    <CardHeader className="bg-white border-bottom px-4 py-3">
                        <h6 className="mb-0 fw-semibold text-uppercase text-muted" style={{ fontSize: 12, letterSpacing: "0.08em" }}>
                            User Information
                        </h6>
                    </CardHeader>
                    <CardBody className="px-4 py-4">
                        <InfoRow label="User ID" value={<code className="text-muted small">{data?.userId}</code>} />
                        <InfoRow label="Full Name" value={data?.fullName} />
                        <InfoRow label="Email" value={<a href={`mailto:${data?.email}`} className="text-primary text-decoration-none">{data?.email}</a>} />
                        <InfoRow label="Phone" value={data?.phone} />
                        <InfoRow label="Portal" value={data?.portal} />
                        <InfoRow
                            label="Status"
                            value={
                                <Badge color={data?.isActive ? "success" : "danger"} pill>
                                    {data?.isActive ? "Active" : "Inactive"}
                                </Badge>
                            }
                        />
                        <InfoRow
                            label="Approved"
                            value={
                                <Badge color={data?.isAdminApproved ? "primary" : "warning"} pill>
                                    {data?.isAdminApproved ? "Yes" : "Pending"}
                                </Badge>
                            }
                        />
                        <InfoRow label="Registered At" value={formatDate(data?.registeredAt)} />
                    </CardBody>
                </Card>

                {/* ── Activation Logs Card ── */}
                <Card className="border-0 shadow-sm">
                    <CardHeader className="bg-white border-bottom px-4 py-3 d-flex align-items-center justify-content-between">
                        <h6 className="mb-0 fw-semibold text-uppercase text-muted" style={{ fontSize: 12, letterSpacing: "0.08em" }}>
                            Activation Logs
                        </h6>
                        <Badge color="secondary" pill>{data?.activationLogs.length}</Badge>
                    </CardHeader>
                    <CardBody className="p-0">
                        {data?.activationLogs.length === 0 ? (
                            <p className="text-muted text-center py-4 mb-0">No activation logs found.</p>
                        ) : (
                            <div className="table-responsive">
                                <Table hover className="mb-0 align-middle">
                                    <thead className="table-light">
                                        <tr>
                                            <th className="ps-4 py-3 text-uppercase small text-muted fw-semibold" style={{ letterSpacing: "0.06em" }}>Action</th>
                                            <th className="py-3 text-uppercase small text-muted fw-semibold" style={{ letterSpacing: "0.06em" }}>Notes</th>
                                            <th className="py-3 text-uppercase small text-muted fw-semibold" style={{ letterSpacing: "0.06em" }}>Performed By</th>
                                            <th className="pe-4 py-3 text-uppercase small text-muted fw-semibold" style={{ letterSpacing: "0.06em" }}>Date & Time</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data?.activationLogs.map((log) => (
                                            <tr key={log.id}>
                                                <td className="ps-4 py-3">
                                                    <ActionBadge action={log.action} />
                                                </td>
                                                <td className="py-3 text-muted small">{log.notes}</td>
                                                <td className="py-3">
                                                    <span className="small fw-medium">{log.performedByUsername}</span>
                                                </td>
                                                <td className="pe-4 py-3 text-muted small">{formatDate(log.createdAt)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </CardBody>
                </Card>
            </Container>

        </div>
    );
};

export default UserPortalDetail;